import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { fetchLivekitParticipants, triggerAdminEvent } from '@/features/live/liveThunks';
import type { Presenter, Speaker } from '@/features/webinar/types';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { stageInviteEventReceived, stageInvitesReconciled } from '@/features/live/liveSlice';
import { presenterIdentity, speakerIdentity } from '@/lib/inviteRequestState';

interface PresentersListProps {
  presenters: Presenter[];
  speakers: Speaker[];
  loading: boolean;
  workspaceId: number;
  webinarId: number;
  sessionId: number;
  sessionIdentifier: string;
  isModeratorLive?: boolean;
  disabledForYoutube?: boolean;
}

export default function PresentersList({
  presenters,
  speakers,
  loading,
  workspaceId,
  webinarId,
  sessionId,
  sessionIdentifier,
  isModeratorLive,
  disabledForYoutube,
}: PresentersListProps) {
  const { t } = useTranslation('webinars');
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!workspaceId || !webinarId || !sessionId) return;
    const run = () => {
      void dispatch(fetchLivekitParticipants({ workspaceId, webinarId, sessionId }));
    };
    run();
    const intervalId = globalThis.setInterval(run, 12_000);
    return () => globalThis.clearInterval(intervalId);
  }, [dispatch, workspaceId, webinarId, sessionId]);

  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);

  const { participantTrackPublications, livekitParticipants, pendingStageInvites } = useAppSelector(
    (s) => s.live,
  );

  const identitiesFromApi = useMemo(
    () =>
      new Set(
        (livekitParticipants ?? [])
          .map((p) => p.identity)
          .filter((id): id is string => typeof id === 'string' && id.length > 0),
      ),
    [livekitParticipants],
  );

  const identitiesFromAdminClient = useMemo(
    () => new Set(Object.keys(participantTrackPublications)),
    [participantTrackPublications],
  );

  const identitiesWithTracks = useMemo(
    () =>
      new Set(
        Object.entries(participantTrackPublications)
          .filter(([, value]) => value === true)
          .map(([identity]) => identity),
      ),
    [participantTrackPublications],
  );

  // La modale d'un invité qui recharge ou ferme l'onglet est perdue : sa sortie de la salle vaut
  // abandon, sinon le bouton resterait sur « En attente » sans issue.
  useEffect(() => {
    dispatch(
      stageInvitesReconciled({
        identitiesInRoom: [...identitiesFromAdminClient],
        identitiesInRoomFromApi: [...identitiesFromApi],
        identitiesWithTracks: [...identitiesWithTracks],
      }),
    );
  }, [dispatch, identitiesFromAdminClient, identitiesFromApi, identitiesWithTracks]);

  /** Liste unifiée : presenters + speakers qui n'ont pas déjà un presenter (évite doublon) */
  const combinedList = useMemo(() => {
    const presenterSpeakerIds = new Set(
      (presenters ?? []).map((p) => p.userId).filter((id) => id != null),
    );
    const speakersOnly = (speakers ?? []).filter((s) => !presenterSpeakerIds.has(s.userId ?? ''));
    return [
      ...(presenters ?? []).map((item) => ({ type: 'presenter' as const, item })),
      ...speakersOnly.map((item) => ({ type: 'speaker' as const, item })),
    ];
  }, [presenters, speakers]);

  const sendJoinRequest = async (
    identity: string,
    input: {
      eventType: 'PRESENTER_JOIN_REQUEST' | 'SPEAKER_JOIN_REQUEST';
      resourceType: 'PRESENTER' | 'SPEAKER';
      resourceId: number;
      payloadJson: string;
    },
  ) => {
    // Posé avant l'appel : un double clic ne doit jamais partir deux fois.
    dispatch(stageInviteEventReceived({ kind: 'requested', identity }));
    const result = await dispatch(
      triggerAdminEvent({ workspaceId, webinarId, sessionIdentifier, input }),
    );
    if (!triggerAdminEvent.fulfilled.match(result)) {
      dispatch(stageInviteEventReceived({ kind: 'declined', identity }));
      toast.error(t('adminLive.presenters.inviteError'));
    }
  };

  const handlePresenterJoin = async (presenter: Presenter) => {
    if (!workspaceId || !webinarId || !sessionId || !sessionIdentifier) {
      return;
    }
    await sendJoinRequest(presenterIdentity(presenter.userId), {
      eventType: 'PRESENTER_JOIN_REQUEST',
      resourceType: 'PRESENTER',
      resourceId: presenter.id,
      payloadJson: JSON.stringify({
        presenterUserId: presenter.userId,
      }),
    });
  };

  const handlePresenterRemove = async (presenter: Presenter) => {
    await dispatch(
      triggerAdminEvent({
        workspaceId,
        webinarId,
        sessionIdentifier,
        input: {
          eventType: 'PRESENTER_REMOVE_REQUEST',
          resourceType: 'PRESENTER',
          resourceId: presenter.id,
          payloadJson: JSON.stringify({
            presenterUserId: presenter.userId,
          }),
        },
      }),
    );
  };

  const handleSpeakerJoin = async (speaker: Speaker, identitySuffix: string) => {
    if (!workspaceId || !webinarId || !sessionIdentifier || !speaker.id) {
      return;
    }

    await sendJoinRequest(speakerIdentity(identitySuffix), {
      eventType: 'SPEAKER_JOIN_REQUEST',
      resourceType: 'SPEAKER',
      resourceId: speaker.id,
      payloadJson: JSON.stringify({
        speakerUserId: identitySuffix,
        displayName: speaker.displayName?.trim() || speaker.guestFullName?.trim() || '',
      }),
    });
  };

  const handleSpeakerRemove = async (speaker: Speaker, identitySuffix: string) => {
    if (!workspaceId || !webinarId || !sessionIdentifier || !speaker.id) {
      return;
    }

    await dispatch(
      triggerAdminEvent({
        workspaceId,
        webinarId,
        sessionIdentifier,
        input: {
          eventType: 'SPEAKER_REMOVE_REQUEST',
          resourceType: 'SPEAKER',
          resourceId: speaker.id,
          payloadJson: JSON.stringify({
            speakerUserId: identitySuffix,
          }),
        },
      }),
    );
  };

  if (loading) {
    return (
      <div className="w-full flex items-center gap-4">
        <p className=" text-gray-500">{t('adminLive.presenters.loading')}</p>
      </div>
    );
  }

  if (disabledForYoutube) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p className="text-gray-500">{t('live.youtube.presenterSingleOnly')}</p>
      </div>
    );
  }

  if (combinedList.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p className=" text-gray-500">{t('adminLive.presenters.empty')}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full   gap-4 ',
        isModeratorLive
          ? 'grid grid-cols-1 sm:grid-cols-2'
          : 'flex-row items-center overflow-x-auto flex',
      )}
      style={{ scrollbarColor: 'currentColor transparent' }}
    >
      {combinedList.map((entry) => {
        const isPresenter = entry.type === 'presenter';
        const presenter = isPresenter ? (entry.item as Presenter) : null;
        const speaker = !isPresenter ? (entry.item as Speaker) : null;
        const displayName = isPresenter
          ? presenter!.displayName ||
            presenter!.overrideFullName ||
            t('adminLive.presenters.defaultName')
          : speaker!.displayName ||
            speaker!.guestFullName ||
            t('adminLive.presenters.defaultNameSpeaker');
        const initials = displayName
          .split(' ')
          .map((n) => n.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);

        const identity = isPresenter
          ? presenterIdentity(presenter!.userId)
          : (() => {
              const lp = livekitParticipants?.find(
                (p) =>
                  p.identity?.startsWith('speaker-') &&
                  ((p as { speakerId?: number }).speakerId === speaker!.id ||
                    (p as { userId?: string }).userId === speaker!.userId),
              );
              if (lp) return lp.identity;
              if (speaker!.userId) {
                const suffix = speaker!.userId.includes('-')
                  ? speaker!.userId.slice(0, 8)
                  : speaker!.userId;
                return speakerIdentity(suffix);
              }
              return '';
            })();
        const identitySuffix = identity?.startsWith('speaker-') ? identity.slice(8) : '';
        const hasTrackPublications = identity
          ? (participantTrackPublications[identity] ?? false)
          : false;
        const isInvitePending = identity !== '' && Object.hasOwn(pendingStageInvites, identity);

        const presentersWithTracksCount = Object.entries(participantTrackPublications).filter(
          ([key, val]) => key.startsWith('presenter-') && val === true,
        ).length;
        const isOnlyPresenterWithTracks =
          isPresenter && hasTrackPublications && presentersWithTracksCount === 1;

        const isSpeakerInRoomFromApi =
          !isPresenter &&
          (livekitParticipants ?? []).some((p) => {
            if (!p.identity?.startsWith('speaker-')) return false;
            const ext = p as { speakerId?: number; userId?: string };
            return (
              ext.speakerId === speaker!.id ||
              String(ext.userId ?? '') === String(speaker!.userId ?? '')
            );
          });

        const isInLivekitRoom =
          (identity !== '' &&
            (identitiesFromApi.has(identity) || identitiesFromAdminClient.has(identity))) ||
          isSpeakerInRoomFromApi;

        return (
          <div
            key={isPresenter ? `p-${presenter!.id}` : `s-${speaker!.id}`}
            className={cn(
              ' bg-white rounded-2xl flex items-center justify-center relative',
              isModeratorLive ? 'w-full h-[128px]' : 'min-w-[230px] w-full h-[128px]',
            )}
          >
            <div
              className={`absolute top-2 right-2 z-20 text-[10px] font-bold uppercase tracking-wide p-1 px-2 rounded-full ${
                isInLivekitRoom ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-500'
              }`}
              title={
                isInLivekitRoom
                  ? t('adminLive.presenters.connected')
                  : t('adminLive.presenters.notConnected')
              }
            >
              {isInLivekitRoom
                ? t('adminLive.presenters.present')
                : t('adminLive.presenters.notPresent')}
            </div>
            <div className="rounded-full size-[64px] bg-gray-200 absolute top-4 left-1/2 -translate-x-1/2">
              <Avatar className="rounded-full size-[64px]  hover:opacity-80 transition-opacity">
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-6  z-10 left-1/2 -translate-x-1/2">
                {hasTrackPublications ? (
                  isOnlyPresenterWithTracks ? (
                    <span className="cursor-not-allowed inline-block">
                      <Button
                        className="shadow w-18 text-sm bg-[#FFD753] hover:bg-[#FFD753]/80"
                        disabled
                      >
                        {t('adminLive.presenters.remove')}
                      </Button>
                    </span>
                  ) : isPresenter ? (
                    <Button
                      className="shadow-lg w-18 text-sm bg-[#FFD753] hover:bg-[#FFD753]/80"
                      onClick={() => handlePresenterRemove(presenter!)}
                    >
                      {t('adminLive.presenters.remove')}
                    </Button>
                  ) : (
                    <Button
                      className="shadow-lg w-18 text-sm bg-[#FFD753] hover:bg-[#FFD753]/80"
                      onClick={() => handleSpeakerRemove(speaker!, identitySuffix)}
                    >
                      {t('adminLive.presenters.remove')}
                    </Button>
                  )
                ) : isInvitePending ? (
                  <Button
                    className="shadow w-18 text-sm bg-gray-200 text-gray-600 disabled:opacity-100"
                    disabled
                    aria-live="polite"
                  >
                    {t('adminLive.presenters.pending')}
                  </Button>
                ) : isPresenter ? (
                  <Button
                    className="shadow-lg w-18 text-sm"
                    onClick={() => handlePresenterJoin(presenter!)}
                    disabled={!isInLivekitRoom}
                    style={{
                      backgroundColor:
                        currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#000000',
                    }}
                  >
                    {t('adminLive.presenters.add')}
                  </Button>
                ) : (
                  <Button
                    className="shadow-lg w-18 text-sm"
                    onClick={() => handleSpeakerJoin(speaker!, identitySuffix)}
                    disabled={!isInLivekitRoom}
                    style={{
                      backgroundColor:
                        currentWorkspace?.visualIdentity?.secondaryBackgroundColor || '#000000',
                    }}
                  >
                    {t('adminLive.presenters.add')}
                  </Button>
                )}
              </div>
            </div>

            <div className="absolute bottom-0 left-0 py-0.5 px-2 bg-black rounded-bl-2xl rounded-tr-2xl">
              <p className="text-white text-xs">{displayName}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
